import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SendOutlined, RobotOutlined } from '@ant-design/icons';
import '../assets/css/home.css';
import { apiPostMessage, apiLawerInfo, apiGetHistoryMessage } from '../request/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { zeusApp } from '../request/index';

interface HistoryMessage {
  id: string;
  dialoguePcId: string;
  isAnswer: number; // 0:用户, 1:AI
  state: string;
  type: string;
  content: string;
  file: any;
  fileSource: any;
  duration: any;
  sendAt: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: string;
  uscc?: string;
  userId?: string;
  appId?: string;
  token?: string;
}

const PAGE_SIZE = 15; // 定义常量，确保前后一致

const Home: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [lawyerucc, setLawyerucc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 标记是否是首次加载
  const [isPageLoading, setIsPageLoading] = useState(true); // 标记页面是否在加载中
  
  const userId = localStorage.getItem('id') || '';
  const userName = localStorage.getItem('userName') || '';
  const token = localStorage.getItem('userToken') || '';
  const appId = zeusApp || '';

  const [connectionStatus, setConnectionStatus] = useState('未连接');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialInputRef = useRef<HTMLTextAreaElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const shouldScrollToBottomRef = useRef(false); // 控制是否滚动到底部
  const typingTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取用户token
  const getToken = () => {
    return localStorage.getItem('userToken') || '';
  };

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (shouldScrollToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // 打字机效果
  const typeWriter = useCallback((fullText: string, messageId: string, speed = 30) => {
    let index = 0;

    const timer = setInterval(() => {
      index++;

      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, text: fullText.slice(0, index) }
            : msg
        )
      );

      if (index >= fullText.length) {
        clearInterval(timer);
        typingTimersRef.current.delete(messageId);
        setIsLoading(false);
        
        // 打字完成后滚动到底部
        shouldScrollToBottomRef.current = true;
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }, speed);
    
    typingTimersRef.current.set(messageId, timer);
  }, [scrollToBottom]);

  // 加载历史记录
  const loadHistoryMessages = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    if (!lawyerucc || isLoadingMoreRef.current) return;
    
    setIsLoadingHistory(true);
    isLoadingMoreRef.current = true;
    
    try {
      const historyInfo = {
        uscc: lawyerucc,
        page: page,
        size: PAGE_SIZE
      };
      
      const res = await apiGetHistoryMessage(historyInfo);
      console.log('历史记录响应:', res);
      
      if (res.data.ret === '0') {
        const historyData = res.data.data;
        setHistoryTotal(historyData.total);
        
        // 判断是否还有更多数据 - 使用实际加载的数量
        const loadedCount = page * PAGE_SIZE;
        const hasMore = loadedCount < historyData.total;
        setHasMoreHistory(hasMore);
        
        // 将历史记录转换为消息格式
        const newHistoryMessages: Message[] = historyData.rows.map((item: HistoryMessage) => ({
          id: item.id,
          text: item.content,
          sender: item.isAnswer === 1 ? 'ai' : 'user',
          timestamp: new Date(item.sendAt),
          type: '1',
          uscc: lawyerucc,
          userId: item.isAnswer === 1 ? undefined : userId
        }));
        
        // 按照时间顺序排序（旧到新）
        const sortedMessages = newHistoryMessages.sort((a, b) => 
          a.timestamp.getTime() - b.timestamp.getTime()
        );
        
        if (page === 1) {
          // 第一页，直接设置
          setMessages(sortedMessages);
          setIsChatStarted(true);
          
          // 首次加载时滚动到底部（最新消息）
          if (isInitialLoad && !isLoadMore) {
            shouldScrollToBottomRef.current = true;
            setTimeout(() => {
              scrollToBottom();
              setIsInitialLoad(false);
            }, 300); // 稍等DOM渲染完成
          }
        } else {
          // 加载更多，将新消息插入到现有消息前面
          setMessages(prev => [...sortedMessages, ...prev]);
          // 加载更多时不滚动
          shouldScrollToBottomRef.current = false;
        }
        
        setHistoryPage(page);
      }
    } catch (error) {
      console.error('加载历史记录失败:', error);
    } finally {
      setIsLoadingHistory(false);
      isLoadingMoreRef.current = false;
    }
  }, [lawyerucc, userId, isInitialLoad, scrollToBottom]);

  // WebSocket连接
  const connectWebSocket = useCallback(() => {
    const token = getToken();
    // 检查是否有token
    if (!token) {
      console.error('用户未登录，无法连接WebSocket');
      setConnectionStatus('未登录，请先登录');
      return;
    }

    const baseUrl = 'wss://api.zeus.ipcn.cn/ws/chat';
    const wsUrl = `${baseUrl}?zeus-token=${encodeURIComponent(token)}&zeus-app=${encodeURIComponent(zeusApp)}`;

    console.log('正在连接WebSocket:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket连接已建立');
        setIsConnected(true);
        setConnectionStatus('已连接');
      };

      socket.onmessage = (event) => {
        try {
          const messageData = JSON.parse(event.data);
          handleWebSocketMessage(messageData);
        } catch (error) {
          console.error('解析WebSocket消息错误:', error);
        }
      };

      socket.onclose = (event) => {
        console.log('WebSocket连接已关闭', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('已断开');

        // 5秒后尝试重连
        setTimeout(() => {
          console.log('尝试重新连接...');
          connectWebSocket();
        }, 5000);
      };

      socket.onerror = (error) => {
        console.error('WebSocket错误:', error);
        setConnectionStatus('连接错误');
        setIsConnected(false);
      };

    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
      setConnectionStatus('连接失败');
    }
  }, []);

  // 加载更多历史记录的函数
  const loadMoreHistory = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreHistory) return;
    
    const nextPage = historyPage + 1;
    await loadHistoryMessages(nextPage, true); // 传入 true 表示是加载更多
  }, [historyPage, hasMoreHistory, loadHistoryMessages]);

  // 初始化加载
  useEffect(() => {
    const init = async () => {
      try {
        // 先获取律师信息
        const res = await apiLawerInfo();
        console.log('律师信息响应:', res);
        
        if (res.data.ret === '0') {
          const uscc = res.data.data.uscc;
          console.log(uscc)
          setLawyerucc(uscc);
          console.log(lawyerucc)
          // 获取历史记录
          await loadHistoryMessages(1, false);
        } else {
          console.error('获取律师信息失败:', res.data.msg);
        }
      } catch (error) {
        console.error('初始化失败:', error);
      } finally {
        // 无论成功与否，都结束页面加载状态
        setIsPageLoading(false);
      }
    };

    // 连接WebSocket
    connectWebSocket();
    
    // 开始初始化
    init();
    
    // 组件卸载时关闭WebSocket连接和清理定时器
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      // 清理所有打字效果定时器
      typingTimersRef.current.forEach((timer) => {
        clearInterval(timer);
      });
      // 清理防抖定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [connectWebSocket, loadHistoryMessages]);

  // 监听滚动事件，实现下拉加载更多
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // 清除之前的定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // 设置防抖，避免频繁触发
      debounceTimerRef.current = setTimeout(() => {
        // 只有确实滚动到顶部时才加载更多
        const isAtTop = container.scrollTop <= 10; // 10px的容差
        const hasScrollContent = container.scrollHeight > container.clientHeight;
        
        // 只有在有更多历史记录、不在加载中、滚动到顶部且有滚动内容时才加载
        if (isAtTop && hasScrollContent && hasMoreHistory && !isLoadingHistory) {
          loadMoreHistory();
        }
      }, 200); // 200ms防抖
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [hasMoreHistory, isLoadingHistory, loadMoreHistory]);

  // 当发送新消息或AI回复时，滚动到底部
  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const handleWebSocketMessage = (messageData: any) => {
    console.log('收到WebSocket消息:', messageData);

    // ===== AI 消息 =====
    if (messageData.type === 'AI' || messageData.sender === 'AI') {
      const fullText = messageData.content || messageData.text || '';
      const messageId = `ai_${Date.now()}`;

      // 关闭 loading
      setIsLoading(false);

      // 插入空消息
      const aiMessage: Message = {
        id: messageId,
        text: '',
        sender: 'ai',
        timestamp: new Date(),
        type: messageData.type || 'AI',
        uscc: lawyerucc
      };

      setMessages(prev => [...prev, aiMessage]);

      // 启动打字机效果
      requestAnimationFrame(() => {
        typeWriter(fullText, messageId, 25);
      });

      return;
    }

    // ===== SYSTEM 消息 =====
    if (messageData.type === 'SYSTEM') {
      const systemMessage: Message = {
        id: `system_${Date.now()}`,
        text: messageData.content || messageData.text || '',
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, systemMessage]);
      
      // 系统消息也滚动到底部
      shouldScrollToBottomRef.current = true;
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      return;
    }

    // ===== ERROR =====
    if (messageData.type === 'ERROR') {
      setIsLoading(false);

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: `系统错误：${messageData.content || messageData.text || '未知错误'}`,
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, errorMessage]);
      
      // 错误消息也滚动到底部
      shouldScrollToBottomRef.current = true;
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  // 发送WebSocket消息
  const sendWebSocketMessage = (content: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket未连接');
      setConnectionStatus('发送失败：未连接');
      return false;
    }

    const message = {
      id: `msg_${Date.now()}`,
      content: content,
      sender: 'user',
      type: 'USER',
      timestamp: Date.now(),
      uscc: lawyerucc,
      userId: userId + '.' + userName,
      token: token,
      appId: appId
    };

    try {
      socketRef.current.send(JSON.stringify(message));
      console.log('已发送消息:', message);
      return true;
    } catch (error) {
      console.error('发送WebSocket消息失败:', error);
      return false;
    }
  };

  // 自动聚焦输入框
  useEffect(() => {
    if (!isChatStarted && initialInputRef.current) {
      initialInputRef.current.focus();
    } else if (isChatStarted && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isChatStarted]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);

    // 调整textarea高度
    const textarea = e.target;
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = 24;
    const padding = 32;
    const lines = Math.max(textarea.value.split('\n').length, 1);
    const calculatedHeight = Math.max(lines * lineHeight + padding, 48);
    const newHeight = Math.min(Math.max(scrollHeight, calculatedHeight), 400);
    
    textarea.style.height = newHeight + 'px';
    
    if (newHeight >= 400) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // 检查是否可以发送消息
      const canSend = inputText.trim() && !isLoading && isConnected;
      
      if (canSend) {
        handleSendMessage();
      }
    }
  };

  // 格式化时间
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading || !isConnected) return;

    const userMessage = inputText.trim();
    setInputText('');

    // 重置输入框高度
    if (initialInputRef.current) {
      initialInputRef.current.style.height = 'auto';
    }
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
    }

    // 如果是第一条消息，切换到聊天状态
    if (!isChatStarted) {
      setIsChatStarted(true);
    }
    
    // 添加用户消息
    const newUserMessage: Message = {
      id: `user_${Date.now()}`,
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
      uscc: lawyerucc,
      userId: userId
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);

    // 开始加载状态
    setIsLoading(true);

    // 用户发送消息后需要滚动到底部
    shouldScrollToBottomRef.current = true;
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    // 优先使用WebSocket发送消息
    let sendSuccess = false;
    if (isConnected) {
      sendSuccess = sendWebSocketMessage(userMessage);
    } else {
      // 如果未连接，尝试重新连接
      const token = getToken();
      if (token) {
        connectWebSocket();
        // 等待1秒后尝试发送
        setTimeout(() => {
          if (isConnected) {
            sendSuccess = sendWebSocketMessage(userMessage);
          } else {
            setConnectionStatus('发送失败：未连接');
            setIsLoading(false);
          }
        }, 1000);
      } else {
        setConnectionStatus('未登录，请先登录');
        setIsLoading(false);
      }
    }

    // 如果WebSocket发送失败，尝试HTTP API
    if (!sendSuccess) {
      console.log('WebSocket发送失败，尝试HTTP API');
      // 可以根据需要启用HTTP备用方案
      // await sendHttpMessage(userMessage);
    }
  };

  // 清空对话
  const clearChat = () => {
    if (window.confirm('确定要清空所有对话吗？')) {
      // 清理所有打字效果定时器
      typingTimersRef.current.forEach((timer) => {
        clearInterval(timer);
      });
      typingTimersRef.current.clear();
      
      setMessages([]);
      if (messages.length > 0) {
        const systemMessage: Message = {
          id: `system_${Date.now()}`,
          text: '对话已清空，请开始新的对话！',
          sender: 'ai',
          timestamp: new Date(),
          type: 'SYSTEM',
          uscc: lawyerucc
        };
        setMessages([systemMessage]);
        
        // 清空对话后滚动到底部
        shouldScrollToBottomRef.current = true;
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  };

  // 重新连接WebSocket
  const reconnectWebSocket = () => {
    const token = getToken();
    if (token) {
      if (socketRef.current) {
        socketRef.current.close();
      }
      setConnectionStatus('重新连接中...');
      connectWebSocket();
    } else {
      setConnectionStatus('未登录，请先登录');
    }
  };

  // 页面加载中
  if (isPageLoading) {
    return (
      <div className="initial-container" style={{ justifyContent: 'center' }}>
        <div className="loading-indicator" style={{ marginBottom: '20px' }}>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          正在加载中...
        </div>
      </div>
    );
  }

  // 渲染初始状态（只有一个输入框且没有历史记录时）
  if (!isChatStarted && messages.length === 0) {
    // 检查是否可以发送消息
    const canSend = inputText.trim() && !isLoading && isConnected;
    
    return (
      <div className="initial-container">
        <div className="initial-header">
          <h1><span>您好，{localStorage.getItem('userName') || '用户'}</span></h1>
        </div>
        <div className="initial-input-container">
          <textarea
            ref={initialInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={!isConnected ? "连接中，请稍后..." : "输入消息..."}
            className="initial-chat-input"
            disabled={!isConnected}
            style={{
              cursor: isConnected ? 'text' : 'not-allowed',
              opacity: isConnected ? 1 : 0.6
            }}
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={!canSend}
              className="initial-send-button"
              style={{
                cursor: canSend ? 'pointer' : 'not-allowed',
                opacity: canSend ? 1 : 0.5
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="initial-input-hint">
          {isConnected ? 
            "按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。" :
            "正在连接服务器，请稍后..."
          }
        </div>
        <div className="connection-status" style={{
          fontSize: '12px',
          color: isConnected ? '#10a37f' : '#ff4d4f',
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
            marginRight: '6px'
          }} />
          <span>连接状态: {connectionStatus}</span>
        </div>
      </div>
    );
  }

  // 渲染聊天状态
  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-title">
          <RobotOutlined style={{ color: '#10a37f', marginRight: '10px' }} />
          <h1>AI助手</h1>
          <div className="connection-indicator" style={{
            marginLeft: '20px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
              marginRight: '5px'
            }} />
            <span>{connectionStatus}</span>
          </div>
        </div>
        <div className="chat-subtitle">
          我是你的智能助手，随时为你提供帮助
          <button
            onClick={clearChat}
            style={{
              marginLeft: '20px',
              fontSize: '12px',
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '2px 8px',
              cursor: 'pointer'
            }}
          >
            清空对话
          </button>
          {historyTotal > 0 && (
            <span style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>
              共 {historyTotal} 条历史记录
            </span>
          )}
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        <div className="messages-list">
          {isLoadingHistory && historyPage === 1 ? (
            <div className="loading-history">
              <div className="loading-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <span>正在加载历史记录...</span>
            </div>
          ) : (
            <>
              {isLoadingHistory && historyPage > 1 && (
                <div className="loading-more-history">
                  <div className="loading-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                  <span>正在加载更多历史记录...</span>
                </div>
              )}
              
              {messages.length === 0 ? (
                <div className="empty-state">
                  <RobotOutlined style={{ fontSize: '48px', color: '#10a37f', marginBottom: '20px' }} />
                  <h3>开始对话</h3>
                  <p>输入消息开始与AI助手对话</p>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`message-item ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
                    >
                      <div className="message-content">
                        {message.type === 'SYSTEM' ? (
                          <div className="system-message" style={{
                            color: '#666',
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            borderLeft: '3px solid #10a37f'
                          }}>
                            <strong>系统提示：</strong>{message.text}
                          </div>
                        ) : (
                          <>
                            <div className="message-text">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.text}
                              </ReactMarkdown>
                            </div>
                            <div className="message-time">
                              {formatTime(message.timestamp)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
          
          {isLoading && (
            <div className="message-item ai-message">
              <div className="message-content">
                <div className="loading-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            ref={chatInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={!isConnected ? "连接中，请稍后..." : "输入消息..."}
            className="chat-input"
            disabled={!isConnected}
            style={{
              cursor: isConnected ? 'text' : 'not-allowed',
              opacity: isConnected ? 1 : 0.6
            }}
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={!isConnected || isLoading}
              className={`send-button ${inputText.trim() && !isLoading && isConnected ? 'active' : ''}`}
              style={{
                cursor: (isConnected && !isLoading) ? 'pointer' : 'not-allowed',
                opacity: (isConnected && !isLoading) ? 1 : 0.5
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="input-hint" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span>
            {isConnected ? 
              "按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。" :
              "正在连接服务器，请稍后..."
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default Home;